import { useState } from 'react';
import {
    InfoBlock,
    TitlePageH1, TabsCustom, useFetchData,
} from '@tap/mui-ui';
import { useNavigate } from 'react-router';
import { Grid, Typography } from '@mui/material';
import AssignUserDialog from './dialogs/AssignUserDialog';
import DeleteIssueDialog from './dialogs/DeleteIssueDialog';
import { EditIssueDialog, IEditIssueDialogResult, IssueFormMode } from './dialogs/EditIssueDialog';
import { userService, issueService, DocumentPlugin, DocumentCode, relationLinkService, EventEntity } from '@tap/data-providers';
import React from 'react';
import {
    ChangeHistoryList,
    ICancelDialogResult, useDialog, useSession, usePage,
} from '@tap/tap-ui';
import IssuePageToolbar, { ToolbarAction } from './issue-page-ui/IssuePageToolbar';
import DynamicFormDialog from './dialogs/DynamicFormDialog';
import {
    AttachmentGrid, CommentList, DocumentProvider, DocumentPropertyList, TimeTrackingLog, TimeTrackingWidget,
    useAttachmentGrid, useDocuemtnSchema, DocumentPageRouteParams, QCResultList,
    useComment,
    DescriptionEditor,
} from '@tap/doc-ui';
import { ErrorBoundary } from 'react-error-boundary';
import { ViewFieldComponent, useRelationLinks, RelationLinkCreateDialog, useAttachments } from '@tap/doc-ui';
import { transformHelper } from '@tap/utils';


/**
 *  Задание (детальная информация о задании) 
 */

export const IssuePage = () => {
    const { pageRouteParams } = usePage<DocumentPageRouteParams>();
    const { documentKey, documentCode, entityId } = pageRouteParams.documentKey;
    const [reloadDocumentIndex, setReloadDocumentIndex] = useState(1);

    const { user } = useSession();
    const issueKey = documentKey;
    const issueId = entityId;

    const navigate = useNavigate();

    const commentState = useComment({ documentKeys: [documentKey] });

    const documentSchema = useDocuemtnSchema(documentCode);
    const techDocumentSchema = useDocuemtnSchema(DocumentCode.TECH);

    const [issueState, issue] = useFetchData(() => {
        return issueService.getIssueByKey(issueKey).then((result) => {

            const userIds = [result.createdBy, result.assigneeId].filter(id => id > 0);

            return userService.getWorkerList({ workerIds: [...new Set(userIds)] })
                .then(users => [...users, { id: -1, lastName: 'Система' }])
                .then(users => {

                    result.author = users.find(u => u.id === result.createdBy);
                    result.assignee = users.find(u => u.id === result.assigneeId);

                    return result;
                });


        });

    }, [issueKey]);

    const handleEditDescriptionOnChange = (text: string) => {
        if (!issue)
            return;

        issueState.setData({ ...issue, description: text });
    }

    const handleCloseDialog = ({ isCancel }: ICancelDialogResult) => {
        if (!isCancel) {
            issueState.fetchData();
        }
    };

    const handleCloseEditDialog = ({ isCancel, mode, newIssueKey }: IEditIssueDialogResult) => {
        if (isCancel)
            return;

        if (mode === IssueFormMode.Clone && newIssueKey) {
            window.open(`/${newIssueKey}`, '_blank').focus();
        }

        issueState.fetchData();
        setReloadDocumentIndex((cv) => cv + 1);
    };

    const handleCloseDeleteDialog = ({ isCancel }: ICancelDialogResult) => {
        if (!isCancel) {
            navigate({ pathname: `/documents/${documentCode}` }, { replace: true });
        }
    };

    const handleCloseRelationLinksDialog = ({ isCancel }: ICancelDialogResult) => {
        if (!isCancel) {
            relationLinksState.fetchData();
        }
    };

    // Диалоги
    const AssignUserReadyDialog = useDialog(AssignUserDialog, handleCloseDialog);
    const DynamicFormReadyDialog = useDialog(DynamicFormDialog, handleCloseDialog);
    const DeleteIssueReadyDialog = useDialog(DeleteIssueDialog, handleCloseDeleteDialog);
    const EditIssueReadyDialog = useDialog(EditIssueDialog, handleCloseEditDialog);
    const RelationLinkCreateReadyDialog = useDialog(RelationLinkCreateDialog, handleCloseRelationLinksDialog);

    const fileUploadCallback = () => {
        issueState.fetchData();
    };


    const { RelationLinksJsx, relationLinksState } = useRelationLinks({ documentKey: issueKey });
    //const { AttachmentsJsx, attachmentsState, hundleOpenSelectFile } = useAttachments({ documentKey: issueKey });
    const attachmentsBlockProps = useAttachments({ documentKey: issueKey, fileUploadCallback });
    const attachmentGridProps = useAttachmentGrid({ documentKey: issueKey, attachmentsBlockProps });

    const handleToolbarAction = (actionCode: ToolbarAction, payload: any): void => {
        if (!issue)
            return;

        switch (actionCode) {
            case ToolbarAction.AssignUser:
                //AssignUserReadyDialog.open({ issueKeys: [issueKey] });
                DynamicFormReadyDialog.open({
                    issue,
                    formProps: {
                        title: 'Назначение исполнителя',
                        button: 'Назначить',
                        fields: ['assignee'],
                        addons: ['comment'],
                    },
                });
                break;
            case ToolbarAction.AddComment:
                commentState.showAddComment();
                break;
            case ToolbarAction.EditIssue:
                EditIssueReadyDialog.open({
                    issue,
                    mode: IssueFormMode.Edit,
                    documentCode: issue.documentCode,
                    timeTrackingValues: payload.document.data.timeTracking,
                });
                break;
            case ToolbarAction.CloneIssue:
                EditIssueReadyDialog.open({
                    issue: { ...issue, title: `CLONE - ${issue.title}` },
                    mode: IssueFormMode.Clone,
                    documentCode: issue.documentCode,
                    timeTrackingValues: payload.document.data.timeTracking,
                });
                break;
            case ToolbarAction.DeleteIssue:
                DeleteIssueReadyDialog.open({ issueKeys: [issueKey] });
                break;
            case ToolbarAction.ClickWorkflowBtn:
                DynamicFormReadyDialog.open({ issue, hiddenDocumentData: { statusCode: payload.wfa.toStatus }, formProps: payload.wfa.toolbarAction.formProps });
                break;
            case ToolbarAction.CreateRelationLink:
                RelationLinkCreateReadyDialog.open({ documentKey: issue.issueKey });
                break;
            case ToolbarAction.AttachFile:
                attachmentsBlockProps.hundleOpenSelectFile();
                break;
        }
    }

    const handleCreateTechIssueClick = (qcResultRow: any) => {
        if (!issue) {
            return;
        }

        const techIssue = {
            title: issue.data.componentName,
            data: {
                orderNumber: issue.data.orderNumber,
                routeCode: issue.data.routeCode,
                vendorCode: issue.data.vendorCode,
                componentName: issue.data.componentName,
            },
        } as any;

        const timeTrackingValues = {
            estimateMin: techDocumentSchema.settings.sla.find(m => m.code === 'responseTime')?.normative,
            leftMin: techDocumentSchema.settings.sla.find(m => m.code === 'responseTime')?.normative,
        }

        EditIssueReadyDialog.open({
            issue: techIssue,
            mode: IssueFormMode.Create,
            documentCode: DocumentCode.TECH,
            timeTrackingValues,
            callback: async (outputData) => {
                if (!outputData.isCancel) {
                    let qcResultList = [...(issue?.data.qcResultList || [])];

                    qcResultList = qcResultList.map((r, id) => {
                        if (id === qcResultRow.id)
                            return {
                                ...r,
                                techIssueKey: outputData.newIssueKey,
                            };
                        else
                            return r;
                    });

                    await issueService.updateIssue(documentKey, {
                        data: {
                            ...issue?.data,
                            qcResultList,
                        },
                    });

                    await relationLinkService.createRelationLink(documentKey, 'related_to', outputData.newIssueKey);

                    await relationLinksState.fetchData();
                    await issueState.fetchData();
                }
            },
        });
    }

    return (
        <DocumentProvider documentKey={documentKey} reloadIndex={reloadDocumentIndex}>
            {!issueState.isLoading && issue &&
                <>
                    <TitlePageH1>{issue.title}</TitlePageH1>

                    {/* Кнопки действий */}
                    <IssuePageToolbar issue={issue} handleAction={handleToolbarAction} />

                    <Grid
                        spacing={2}
                        container
                        alignItems='flex-start'
                    >
                        <Grid item
                            xs={12}
                            sm={12}
                            md={8}
                            container
                            spacing={2}
                        >
                            <InfoBlock
                                title={'Детали задачи'}
                                xs={12}
                                sm={12}
                                md={12}
                            >
                                <DocumentPropertyList
                                    documentSchema={documentSchema}
                                    fieldListKey={'mainInfoFields'}
                                    document={issue}
                                    md={6}
                                    setDocument={issueState.setData} />
                            </InfoBlock>

                            <InfoBlock
                                title={'Описание'}
                                xs={12}
                                sm={12}
                                md={12}
                            >
                                <DescriptionEditor
                                    initialValue={issue.description || ''}
                                    onSave={(description) => handleEditDescriptionOnChange(description)}
                                />
                            </InfoBlock>

                            {issue && documentSchema.settings.plugins?.includes(DocumentPlugin.QCResult) && issue.statusCode !== 'new' && (
                                <InfoBlock
                                    title={'Результаты контроля'}
                                    xs={12}
                                    sm={12}
                                    md={12}
                                >
                                    <ErrorBoundary fallback={<div>Произошла непредвиденная ошибка</div>}>
                                        <QCResultList
                                            issueState={issueState}
                                            relationLinksState={relationLinksState}
                                            handleCreateTechIssueClick={handleCreateTechIssueClick}
                                        />
                                    </ErrorBoundary>

                                </InfoBlock>

                            )}

                            {issue && documentSchema.settings.plugins?.includes(DocumentPlugin.AttachmentWithTemplate) && (
                                <InfoBlock
                                    title={
                                        issue?.data.type === 'change-wip'
                                            ? documentSchema.settings.attachmentGridProps2.widgetTitle
                                            : documentSchema.settings.attachmentGridProps.widgetTitle
                                    }
                                    xs={12}
                                    sm={12}
                                    md={12}
                                >
                                    <ErrorBoundary fallback={<div>Произошла непредвиденная ошибка</div>}>
                                        <AttachmentGrid
                                            {...attachmentGridProps}
                                            document={issue}
                                        />
                                    </ErrorBoundary>

                                </InfoBlock>

                            )}


                            <InfoBlock
                                title={'Вложенные файлы'}
                                xs={12}
                                sm={12}
                                md={12}
                            >
                                {attachmentsBlockProps.AttachmentsJsx}
                            </InfoBlock>


                            {!!relationLinksState.data?.length && (
                                <InfoBlock
                                    title={'Ссылки'}
                                    xs={12}
                                    sm={12}
                                    md={12}
                                >
                                    {RelationLinksJsx}
                                </InfoBlock>
                            )}

                            <InfoBlock title='Активность'>
                                <TabsCustom
                                    tabsArray={[
                                        {
                                            tabLabel: <Typography sx={{ textTransform: 'none' }}>Комментарии</Typography>,
                                            tabComponent: <CommentList
                                                commentState={commentState}
                                                currentUser={user}
                                                documentKey={issueKey}
                                            />,
                                        },
                                        {
                                            tabLabel: <Typography sx={{ textTransform: 'none' }}>История изменений</Typography>,
                                            tabComponent: <ChangeHistoryList
                                                apiParams={{
                                                    schemaName: 'doc_db',
                                                    entity: EventEntity.Issue,
                                                    entityId: issueId,
                                                }}
                                                slotProps={{
                                                    enumValue: {
                                                        enumDict: { customIssueStatus: transformHelper.arr2dict(documentSchema.settings.statusList, (s) => s.code) },
                                                    },
                                                    jsonValue: {
                                                        schemaDict: { customIssueSchema: documentSchema.settings },
                                                    },
                                                }}
                                                customComponents={{
                                                    documentCustomField: ViewFieldComponent,
                                                }}
                                            />,
                                        },
                                        {
                                            tabLabel: <Typography sx={{ textTransform: 'none' }}>Журнал работ</Typography>,
                                            tabComponent: <TimeTrackingLog />,
                                        },
                                    ]}
                                />
                            </InfoBlock>
                        </Grid>

                        {/* ASAID */}
                        <Grid item
                            xs={12}
                            sm={12}
                            md={4}
                            container
                            spacing={2}
                            alignItems='flex-start'>
                            <InfoBlock
                                title={'Люди'}
                                xs={12}
                                sm={12}
                                md={12}
                            >
                                <DocumentPropertyList
                                    documentSchema={documentSchema}
                                    fieldListKey={'userInfoFields'}
                                    document={issue}
                                    setDocument={issueState.setData} />
                            </InfoBlock>

                            <InfoBlock
                                title={'Даты'}
                                xs={12}
                                sm={12}
                                md={12}
                            >
                                <DocumentPropertyList
                                    documentSchema={documentSchema}
                                    fieldListKey={'dateInfoFields'}
                                    document={issue}
                                    setDocument={issueState.setData} />
                            </InfoBlock>

                            <InfoBlock
                                title={'Учёт времени'}
                                xs={12}
                                sm={12}
                                md={12}
                            >
                                <TimeTrackingWidget />
                            </InfoBlock>
                        </Grid>
                    </Grid>

                    <AssignUserReadyDialog.Component />
                    <DynamicFormReadyDialog.Component />
                    <DeleteIssueReadyDialog.Component />
                    <EditIssueReadyDialog.Component />
                    <RelationLinkCreateReadyDialog.Component />
                </>
            }
        </DocumentProvider>
    );
}
